// hooks/useReceivedEndowment.js
import { useState, useEffect } from 'react';
import { RECIPIENT_ENDPOINTS, STREAM_ENDPOINTS } from '../utils/constants';
import { useModals } from '../hooks/useModals';
import { viewBankCard } from '../services/bankcard.service';
import { viewPassword } from '../services/password.service';
import { viewPhrase } from '../services/blockchain.service';

export const useReceivedEndowment = () => {
  // Main state
  const [inheritances, setInheritances] = useState([]);
  const [activeInheritanceId, setActiveInheritanceId] = useState(null);
  const [currentInheritance, setCurrentInheritance] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  
  // Authentication states
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [needsPasswordChange, setNeedsPasswordChange] = useState(false);
  
  // Alert state
  const [alert, setAlert] = useState({
    show: false,
    message: '',
    variant: 'success'
  });

    const {
      openViewFileModal,
      openViewPasswordModal,
      openViewCardModal,
      openViewBlockchainModal,
    } = useModals();

  // Check authentication status on mount
  useEffect(() => {
    const isAuth = sessionStorage.getItem('inheritanceAuthenticated') === 'true';
    setIsAuthenticated(isAuth);
    
    if (isAuth) {
      loadInheritances();
    } else {
      setIsLoading(false);
    }
  }, []);

  // Show alert message helper
  const showAlert = (message, variant = 'success', duration = 5000) => {
    setAlert({ show: true, message, variant });
    
    if (duration > 0) {
      setTimeout(() => {
        setAlert(prev => ({ ...prev, show: false }));
      }, duration);
    }
  };

  // Load all inheritances for the recipient
  const loadInheritances = async () => {
    setIsLoading(true);
    
    try {
      const recipientInfo = JSON.parse(sessionStorage.getItem('recipientInfo') || '{}');
      const recipientId = recipientInfo.id;
      
      if (!recipientId) {
        throw new Error('Recipient ID not found. Please log in again.');
      }
      
      const response = await fetch(RECIPIENT_ENDPOINTS.GET_INHERITANCES, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${sessionStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ recipientId })
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data && Array.isArray(data)) {
        setInheritances(data);
        
        // Auto-select inheritance logic
        if (data.length > 0) {
          let inheritanceToSelect = null;
          
          // Check if there's a specific inheritance ID in the URL
          const params = new URLSearchParams(window.location.search);
          const urlInheritanceId = params.get('id');
          
          if (urlInheritanceId) {
            // Try to find the inheritance with the URL ID
            inheritanceToSelect = data.find(inheritance => inheritance.id === urlInheritanceId);
          }
          
          // If no URL ID or inheritance not found, select the first available one
          if (!inheritanceToSelect) {
            // Prioritize pending acknowledgment inheritances first, then available ones
            inheritanceToSelect = data.find(inheritance => !inheritance.is_accepted && !inheritance.is_declined) ||
                                 data.find(inheritance => inheritance.is_accepted) ||
                                 data[0]; // Fallback to first inheritance
          }
          
          if (inheritanceToSelect) {
            await selectInheritance(inheritanceToSelect);
          }
        }
      } else {
        showAlert('No inheritances found or invalid response format.', 'warning');
      }
    } catch (error) {
      console.error('Error loading inheritances:', error);
      showAlert(`Error loading inheritances: ${error.message}`, 'danger');
    } finally {
      setIsLoading(false);
    }
  };

  // Select an inheritance to view
  const selectInheritance = async (inheritance) => {
    setIsLoading(true);
    setActiveInheritanceId(inheritance.id);
    
    try {
      const inheritanceToken = sessionStorage.getItem(`token_${inheritance.id}`) || 
                              sessionStorage.getItem('token');
      
      const data = {
        bucket: "inheritance",
        fileName: inheritance.filename + ".txt",
        inheritanceId: inheritance.id
      };
      
      const response = await fetch(STREAM_ENDPOINTS.FILE, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${inheritanceToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const text = await response.text();
      if (!text || text.trim() === '') {
        throw new Error('Empty response received from server');
      }
      
      const lines = text.split('\n');
      const inheritanceData = {};
      
      lines.forEach(line => {
        const [key, value] = line.split(': ').map(item => item.trim());
        if (key && value) {
          inheritanceData[key] = value;
        }
      });
      
      if (!inheritanceData['Selected Items']) {
        throw new Error('Selected Items not found in inheritance data');
      }
      
      // Decrypt the selected items
      const CryptoJS = require('crypto-js');
      try {
        const decryptedSelectedItems = CryptoJS.AES.decrypt(
          inheritanceData['Selected Items'], 
          'k1aY2nI0s4a8Y2nI0s4a8mi0109sdrtw2'
        ).toString(CryptoJS.enc.Utf8);
        
        let selectedItems;
        try {
          selectedItems = JSON.parse(decryptedSelectedItems);
        } catch (parseError) {
          console.error('Error parsing selected items:', parseError);
          throw new Error('Invalid format for decrypted Selected Items');
        }
        
        const structuredInheritance = {
          id: inheritance.id,
          alias: inheritanceData['Alias'] || inheritance.filename,
          account: inheritanceData['Inheritance Account'] || '',
          duration: inheritanceData['Duration'] || '',
          filename: inheritance.filename,
          creator_email: inheritance.creator_email,
          expiration_date: inheritance.expiration_date,
          release_date: inheritance.release_date,
          is_accepted: inheritance.is_accepted,
          is_declined: inheritance.is_declined,
          selectedItems: selectedItems || { files: [], passwords: [], banking: [], blockchain: [] },
          token: inheritanceToken
        };
        
        setCurrentInheritance(structuredInheritance);
        
      } catch (decryptError) {
        console.error('Decryption error:', decryptError);
        throw new Error('Failed to decrypt inheritance data');
      }
      
    } catch (error) {
      console.error('Error fetching inheritance:', error);
      showAlert(`Error loading inheritance: ${error.message}`, 'danger');
      setActiveInheritanceId(null);
      
      // Set current inheritance to basic info without content
      const basicInheritance = {
        id: inheritance.id,
        filename: inheritance.filename,
        creator_email: inheritance.creator_email,
        expiration_date: inheritance.expiration_date,
        release_date: inheritance.release_date,
        is_accepted: inheritance.is_accepted,
        is_declined: inheritance.is_declined,
        selectedItems: { files: [], passwords: [], banking: [], blockchain: [] },
        token: sessionStorage.getItem(`token_${inheritance.id}`) || sessionStorage.getItem('token')
      };
      setCurrentInheritance(basicInheritance);
    } finally {
      setIsLoading(false);
    }
  };

  // Check inheritance availability and status
  const checkInheritanceStatus = (inheritance) => {
    if (!inheritance) return { isAvailable: false, statusType: 'unknown' };

    const now = new Date();
    const releaseDate = new Date(inheritance.release_date);
    const expirationDate = new Date(inheritance.expiration_date);

    if (inheritance.is_declined) {
      return { isAvailable: false, statusType: 'declined' };
    }

    if (expirationDate < now) {
      return { isAvailable: false, statusType: 'expired' };
    }

    if (releaseDate > now) {
      return { isAvailable: false, statusType: 'not_released' };
    }

    if (!inheritance.is_accepted) {
      return { isAvailable: true, statusType: 'pending_acknowledgment' };
    }

    return { isAvailable: true, statusType: 'available' };
  };

  // Handle authentication success
  const handleAuthSuccess = (data) => {
    
    // Store all tokens including inheritance-specific ones
    if (data.token) {
      sessionStorage.setItem('token', data.token);
      
      for (const key in data) {
        if (key.startsWith('token_')) {
          sessionStorage.setItem(key, data[key]);
        }
      }
    }
    
    // Store recipient info
    if (data.recipientInfo) {
      sessionStorage.setItem('recipientInfo', JSON.stringify(data.recipientInfo));
    }
    
    if (data.inheritanceInfo) {
      sessionStorage.setItem('inheritanceInfo', JSON.stringify(data.inheritanceInfo));
    }
    
    if (data.inheritances && data.inheritances.length > 0) {
      sessionStorage.setItem('inheritances', JSON.stringify(data.inheritances));
    }

    if (data.loginPassword) {
  sessionStorage.setItem('loginPassword', data.loginPassword);
}
    
    // Store temporary password if provided (for password change)
    if (data.temporaryPassword) {
      sessionStorage.setItem('temporaryPassword', data.temporaryPassword);
    }
    
    // Store current password if this is for password change flow
    if (data.currentPassword) {
      sessionStorage.setItem('temporaryPassword', data.currentPassword);
    }
    
    // Set active inheritance ID from URL or data
    const params = new URLSearchParams(window.location.search);
    const urlInheritanceId = params.get('id');
    
    if (urlInheritanceId) {
      sessionStorage.setItem('activeInheritanceId', urlInheritanceId);
      setActiveInheritanceId(urlInheritanceId);
    } else if (data.activeInheritanceId || (data.inheritanceInfo && data.inheritanceInfo.id)) {
      const activeId = data.activeInheritanceId || data.inheritanceInfo.id;
      sessionStorage.setItem('activeInheritanceId', activeId);
      setActiveInheritanceId(activeId);
    }
    
    sessionStorage.setItem('inheritanceAuthenticated', 'true');
    setIsAuthenticated(true);
    
    // Check if password change is required
    if (data.recipientInfo && !data.recipientInfo.isPasswordChanged) {
      setNeedsPasswordChange(true);
    } else {
      setNeedsPasswordChange(false);
      loadInheritances();
    }
  };

  // Handle password change success
  const handlePasswordChangeSuccess = () => {
    setNeedsPasswordChange(false);
    loadInheritances();
  };

  // Handle acknowledgment success
  const handleAcknowledgmentSuccess = () => {
    if (currentInheritance) {
      const updatedInheritance = { ...currentInheritance, is_accepted: true };
      setCurrentInheritance(updatedInheritance);
      
      const updatedInheritances = inheritances.map(item => 
        item.id === updatedInheritance.id ? { ...item, is_accepted: true } : item
      );
      setInheritances(updatedInheritances);
    }
    
    // Reload inheritances to get fresh data
    loadInheritances();
  };

  // Handle acknowledge inheritance
  const handleAcknowledge = async () => {
    if (!currentInheritance) return false;

    try {
      const recipientInfo = JSON.parse(sessionStorage.getItem('recipientInfo') || '{}');
      const username = recipientInfo.username;
      
      if (!username || !currentInheritance.id) {
        throw new Error('Missing required information');
      }
      
      const inheritanceToken = currentInheritance.token || 
                              sessionStorage.getItem(`token_${currentInheritance.id}`) || 
                              sessionStorage.getItem('token');
      
      const response = await fetch(RECIPIENT_ENDPOINTS.ACKNOWLEDGE_INHERITANCE, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${inheritanceToken}`
        },
        body: JSON.stringify({
          username,
          inheritanceId: currentInheritance.id,
          termsAccepted: true
        }),
      });
      
      const contentType = response.headers.get("content-type");
      if (contentType && contentType.indexOf("application/json") !== -1) {
        const data = await response.json();
        
        if (data.error) {
          throw new Error(data.error);
        } else {
          handleAcknowledgmentSuccess();
          showAlert('Endowment acknowledged successfully!', 'success');
          return true;
        }
      } else {
        const text = await response.text();
        console.error('Non-JSON response:', text);
        throw new Error('Unexpected response from server');
      }
    } catch (error) {
      console.error('Acknowledgment failed:', error);
      showAlert(`Acknowledgment failed: ${error.message}`, 'danger');
      return false;
    }
  };

  // Handle decline inheritance  
  const handleDecline = async (confirmationText) => {
    if (!currentInheritance) return false;

    try {
      const recipientInfo = JSON.parse(sessionStorage.getItem('recipientInfo') || '{}');
      const username = recipientInfo.username;
      
      if (!username || !currentInheritance.id) {
        throw new Error('Missing required information');
      }
      
      const response = await fetch(RECIPIENT_ENDPOINTS.REJECT_INHERITANCE, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${sessionStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          username,
          inheritanceId: currentInheritance.id,
          confirmationText
        })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        showAlert('Endowment declined successfully.', 'success');
        loadInheritances();
        return true;
      } else {
        throw new Error(data.error || 'Failed to decline Endowment');
      }
    } catch (error) {
      console.error('Decline failed:', error);
      showAlert(`Error declining inheritance: ${error.message}`, 'danger');
      return false;
    }
  };

  // Handle view item
const handleViewItem = async (itemName, itemType) => {
  try {
    // Determine bucket based on itemType
    let bucket;
    switch (itemType.toLowerCase()) {
      case 'files':
        bucket = 'files';
        break;
      case 'passwords':
        bucket = 'passwords';
        break;
      case 'banking':
        bucket = 'bankcards';
        break;
      case 'blockchain':
        bucket = 'blockchain';
        break;
      default:
        throw new Error(`Invalid item type: ${itemType}`);
    }

    // Get inheritance token
    let inheritanceToken;
    if (currentInheritance && currentInheritance.token) {
      inheritanceToken = currentInheritance.token;
    } else {
      inheritanceToken = sessionStorage.getItem(`token_${activeInheritanceId}`) ||
                         sessionStorage.getItem('token');
    }

    if (itemType.toLowerCase() === 'files') {
      // For files: fetch blob
      const response = await fetch(STREAM_ENDPOINTS.FILE, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${inheritanceToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          bucket: bucket,
          fileName: itemName,
          inheritanceId: activeInheritanceId
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const blob = await response.blob();
      const contentType = response.headers.get('Content-Type') || getContentTypeFromFileName(itemName);
      const url = URL.createObjectURL(blob);

      let textContent = null;
      if (contentType.startsWith('text/')) {
        textContent = await blob.text();
      }

      const itemWithData = {
        fileName: itemName,
        name: itemName,
        originalName: itemName,
        type: itemType.toLowerCase(),
        bucket: bucket,
        contentType: contentType,
        url: url,
        blob: blob,
        textContent: textContent
      };

      openViewFileModal(itemWithData);
    } else {
      // For passwords, banking, blockchain — decrypt
      let decryptedData;
      switch (itemType.toLowerCase()) {
        case 'passwords':
          decryptedData = await viewPassword(itemName, inheritanceToken);
          break;
        case 'banking':
          decryptedData = await viewBankCard(itemName, inheritanceToken);
          break;
        case 'blockchain':
          decryptedData = await viewPhrase(itemName, inheritanceToken);
          break;
      }


      const itemWithData = {
        name: itemName,
        data: decryptedData,
        type: itemType.toLowerCase(),
        originalName: itemName,
        ...decryptedData
      };

      switch (itemType.toLowerCase()) {
        case 'passwords':
          openViewPasswordModal(itemWithData);
          break;
        case 'banking':
          openViewCardModal(itemWithData);
          break;
        case 'blockchain':
          openViewBlockchainModal(itemWithData);
          break;
      }
    }
  } catch (error) {
    console.error('Error viewing item:', error);
    showAlert(`Error viewing item: ${error.message}`, 'danger');
    throw error;
  }
};

  const getContentTypeFromFileName = (fileName) => {
    const extension = fileName.split('.').pop().toLowerCase();

    const mimeTypes = {
      // Images
      'jpg': 'image/jpeg',
      'jpeg': 'image/jpeg',
      'png': 'image/png',
      'gif': 'image/gif',
      'bmp': 'image/bmp',
      'svg': 'image/svg+xml',

      // Documents
      'pdf': 'application/pdf',
      'doc': 'application/msword',
      'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'xls': 'application/vnd.ms-excel',
      'xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'ppt': 'application/vnd.ms-powerpoint',
      'pptx': 'application/vnd.openxmlformats-officedocument.presentationml.presentation',

      // Text
      'txt': 'text/plain',
      'html': 'text/html',
      'css': 'text/css',
      'js': 'text/javascript',
      'json': 'application/json',
      'xml': 'text/xml',

      // Audio
      'mp3': 'audio/mpeg',
      'wav': 'audio/wav',
      'ogg': 'audio/ogg',
      'flac': 'audio/flac',

      // Video
      'mp4': 'video/mp4',
      'avi': 'video/avi',
      'mov': 'video/quicktime',
      'wmv': 'video/x-ms-wmv',
      'mkv': 'video/x-matroska',

      // Archives
      'zip': 'application/zip',
      'rar': 'application/x-rar-compressed',
      '7z': 'application/x-7z-compressed'
    };

    return mimeTypes[extension] || 'application/octet-stream';
  };

  // Handle file download
  const handleDownloadFile = async (itemName) => {
    try {
      let inheritanceToken;
      if (currentInheritance && currentInheritance.token) {
        inheritanceToken = currentInheritance.token;
      } else {
        inheritanceToken = sessionStorage.getItem(`token_${activeInheritanceId}`) || 
                          sessionStorage.getItem('token');
      }
      
      const data = {
        bucket: 'files',
        fileName: itemName,
        inheritanceId: activeInheritanceId
      };
      
      const response = await fetch(STREAM_ENDPOINTS.FILE, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${inheritanceToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      let downloadFileName = itemName;
      const contentDispositionHeader = response.headers.get('Content-Disposition');
      if (contentDispositionHeader) {
        const filenameMatch = contentDispositionHeader.match(/filename="?(.+)"?/i);
        if (filenameMatch && filenameMatch.length > 1) {
          downloadFileName = filenameMatch[1].replace(/['"]/g, '');
        }
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = downloadFileName;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      showAlert(`File "${downloadFileName}" downloaded successfully.`, 'success');
    } catch (error) {
      console.error('Error downloading file:', error);
      showAlert(`Error downloading file: ${error.message}`, 'danger');
      throw error;
    }
  };

  // Handle logout
  const handleLogout = () => {
    
    // Clear all session storage
    const keysToRemove = [
      'token', 'recipientInfo', 'creatorInfo', 'inheritanceInfo', 
      'inheritances', 'activeInheritanceId', 'isPasswordChanged', 
      'isAcknowledged', 'inheritanceAuthenticated'
    ];
    
    keysToRemove.forEach(key => sessionStorage.removeItem(key));
    
    // Clear all inheritance-specific tokens
    for (let i = sessionStorage.length - 1; i >= 0; i--) {
      const key = sessionStorage.key(i);
      if (key && key.startsWith('token_')) {
        sessionStorage.removeItem(key);
      }
    }
    
    // Reset state
    setActiveInheritanceId(null);
    setCurrentInheritance(null);
    setInheritances([]);
    setIsAuthenticated(false);
    setNeedsPasswordChange(false);
  };

  return {
    // State
    inheritances,
    activeInheritanceId,
    currentInheritance,
    isLoading,
    isAuthenticated,
    needsPasswordChange,
    alert,

    // Setters
    setAlert,
    setIsLoading,

    // Actions
    loadInheritances,
    selectInheritance,
    checkInheritanceStatus,
    handleAuthSuccess,
    handlePasswordChangeSuccess,
    handleAcknowledgmentSuccess,
    handleAcknowledge,
    handleDecline,
    handleViewItem,
    handleDownloadFile,
    handleLogout,
    showAlert
  };
};